import type { useLanguage } from "@/context/language"
import { dashboardKpiLabels } from "@/i18n/datox-kpi"
import { REPORT_DESIGNER_ENGLISH } from "@/i18n/datox-reports"

// The dashboard renderer's copy, in one place: the classic builder (editor and report studio), the V2 report
// workspace's read-only canvas and the public (published) dashboard all pass `dashboardLabels(t)`.
export function dashboardLabels(t: ReturnType<typeof useLanguage>["t"]) {
  return {
    kpi: dashboardKpiLabels(t),
    designer: Object.fromEntries((Object.keys(REPORT_DESIGNER_ENGLISH) as (keyof typeof REPORT_DESIGNER_ENGLISH)[]).map((key) => [key.replace("datox.designer.", ""), t(key)])),
    notConnected: t("datox.report.notConnected"),
    noValues: t("datox.report.noValues"),
    exportPages: t("datox.report.exportPages"),
    exportIntro: t("datox.report.exportIntro"),
    exportSelectAll: t("datox.report.exportSelectAll"),
    exportClearSelection: t("datox.report.exportClearSelection"),
    exportSelectionSummary: (selected: number, total: number) => t("datox.report.exportSelectionSummary", { selected, total }),
    exportPageNumber: (number: number) => t("datox.report.exportPageNumber", { number }),
    exportDetails: t("datox.report.exportDetails"),
    exportPreparing: t("datox.report.exportPreparing"),
    exportPreparingHint: t("datox.report.exportPreparingHint"),
    exportEmptySelection: t("datox.report.exportEmptySelection"),
    exportSnapshotHint: t("datox.report.exportSnapshotHint"),
    exportFailed: t("datox.report.exportFailed"),
    exportDraft: t("datox.report.exportDraft"),
    // Bobur's server-PDF dialog (serializeDashboardPdf) reads these flat keys; ours reads `pdf` below.
    pdfPreview: t("datox.dashboard.pdf.preview"),
    pdfOrientation: t("datox.dashboard.pdf.orientation"),
    pdfMargin: t("datox.dashboard.pdf.margin"),
    pdfTextSize: t("datox.dashboard.pdf.textSize"),
    pdfScale: t("datox.dashboard.pdf.scale"),
    pdfGray: t("datox.dashboard.pdf.gray"),
    pdfAllRows: t("datox.dashboard.pdf.allRows"),
    pdfTableDensity: t("datox.dashboard.pdf.tableDensity"),
    pdfOriginal: t("datox.dashboard.pdf.original"),
    pdfCompact: t("datox.dashboard.pdf.compact"),
    pdfNormal: t("datox.dashboard.pdf.normal"),
    pdfLarge: t("datox.dashboard.pdf.large"),
    pdfAccent: t("datox.dashboard.pdf.accent"),
    pdfWidgetAdjustments: t("datox.dashboard.pdf.widgetAdjustments"),
    pdfWidgetHelp: t("datox.dashboard.pdf.widgetHelp"),
    pdfWidgetSelect: t("datox.dashboard.pdf.widgetSelect"),
    pdfWidgetTitleSize: t("datox.dashboard.pdf.widgetTitleSize"),
    pdfWidgetContentSize: t("datox.dashboard.pdf.widgetContentSize"),
    pdfKpiScale: t("datox.dashboard.pdf.kpiScale"),
    pdfWidgetReset: t("datox.dashboard.pdf.widgetReset"),
    logoAlt: t("lesha.logo.alt"),
    dashboardTitle: t("datox.dashboard.title"),
    createDashboard: t("datox.dashboard.create"),
    renameDashboard: t("datox.dashboard.renameDashboard"),
    deleteDashboard: t("datox.dashboard.deleteDashboard"),
    deleteDashboardConfirm: t("datox.dashboard.deleteDashboardConfirm"),
    preview: t("datox.dashboard.preview"),
    publishedStatus: t("datox.dashboard.publishedStatus"),
    unpublishedStatus: t("datox.dashboard.unpublishedStatus"),
    publishDashboard: t("datox.dashboard.publishDashboard"),
    unpublishDashboard: t("datox.dashboard.unpublishDashboard"),
    publishDashboardCopied: t("datox.dashboard.publishDashboardCopied"),
    openLink: t("datox.dashboard.openLink"),
    copyLink: t("datox.dashboard.copyLink"),
    copied: t("datox.dashboard.copied"),
    done: t("common.close"),
    clients: t("datox.dashboard.clients"),
    clientsDescription: t("datox.dashboard.clientsDescription"),
    client: t("datox.dashboard.client"),
    clientTable: t("datox.dashboard.clientTable"),
    clientColumn: t("datox.dashboard.clientColumn"),
    clientValues: t("datox.dashboard.clientValues"),
    noClientValues: t("datox.dashboard.noClientValues"),
    allClients: t("datox.dashboard.allClients"),
    column: t("datox.dashboard.column"),
    exportMenu: t("datox.dashboard.exportMenu"),
    exportPdf: t("datox.dashboard.exportPdf"),
    pdf: {
      title: t("datox.dashboard.pdf.title"),
      close: t("common.close"),
      export: t("datox.dashboard.exportPdf"),
      orientation: t("datox.dashboard.pdf.orientation"),
      landscape: t("datox.dashboard.landscape"),
      portrait: t("datox.dashboard.portrait"),
      scope: t("datox.dashboard.pdf.scope"),
      activeTab: t("datox.dashboard.pdf.activeTab"),
      allTabs: t("datox.dashboard.pdf.allTabs"),
      margin: t("datox.dashboard.pdf.margin"),
      millimeters: t("datox.dashboard.pdf.millimeters"),
      scale: t("datox.dashboard.pdf.scale"),
      textSize: t("datox.dashboard.pdf.textSize"),
      widgetAdjustments: t("datox.dashboard.pdf.widgetAdjustments"),
      widgetHelp: t("datox.dashboard.pdf.widgetHelp"),
      widgetSelect: t("datox.dashboard.pdf.widgetSelect"),
      widgetOption: t("datox.dashboard.pdf.widgetOption"),
      kpiValueScale: t("datox.dashboard.pdf.kpiValueScale"),
      widgetTitleSize: t("datox.dashboard.pdf.widgetTitleSize"),
      widgetContentSize: t("datox.dashboard.pdf.widgetContentSize"),
      widgetWrapTitle: t("datox.dashboard.pdf.widgetWrapTitle"),
      widgetWrapText: t("datox.dashboard.pdf.widgetWrapText"),
      widgetWrapCells: t("datox.dashboard.pdf.widgetWrapCells"),
      widgetReset: t("datox.dashboard.pdf.widgetReset"),
      compact: t("datox.dashboard.pdf.compact"),
      normal: t("datox.dashboard.pdf.normal"),
      large: t("datox.dashboard.pdf.large"),
      original: t("datox.dashboard.pdf.original"),
      accent: t("datox.dashboard.pdf.accent"),
      gray: t("datox.dashboard.pdf.gray"),
      allRows: t("datox.dashboard.pdf.allRows"),
      allRowsHelp: t("datox.dashboard.pdf.allRowsHelp"),
      tableDensity: t("datox.dashboard.pdf.tableDensity"),
      reset: t("datox.dashboard.pdf.reset"),
      exporting: t("datox.dashboard.pdf.exporting"),
      preparing: t("datox.dashboard.pdf.preparing"),
      page: t("datox.dashboard.pdf.page"),
      retry: t("datox.dashboard.pdf.retry"),
      loadFailed: t("datox.dashboard.pdf.loadFailed"),
      previewFailed: t("datox.dashboard.pdf.previewFailed"),
      exportFailed: t("datox.dashboard.pdf.exportFailed"),
    },
    shareAccess: t("datox.dashboard.shareAccess"),
    reviewers: t("datox.dashboard.reviewers"),
    reviewerName: t("datox.dashboard.reviewerName"),
    reviewerEmail: t("datox.dashboard.reviewerEmail"),
    reports: t("datox.dashboard.reports"),
    status: t("datox.dashboard.reviewStatus"),
    notSent: t("datox.dashboard.notSent"),
    sent: t("datox.dashboard.sent"),
    duplicateEmail: t("datox.dashboard.duplicateEmail"),
    addAnotherReviewer: t("datox.dashboard.addAnotherReviewer"),
    reviewersPlaceholder: t("datox.dashboard.reviewersPlaceholder"),
    sendShareAccess: t("datox.dashboard.sendShareAccess"),
    sendingShareAccess: t("datox.dashboard.sendingShareAccess"),
    tidyLayout: t("datox.dashboard.tidyLayout"),
    renameTab: t("datox.dashboard.renameTab"),
    duplicateTab: t("datox.dashboard.duplicateTab"),
    deleteTab: t("datox.dashboard.deleteTab"),
    rename: t("datox.dashboard.rename"),
    name: t("datox.dashboard.nameField"),
    doubleClickRename: t("datox.dashboard.doubleClickRename"),
    saving: t("datox.dashboard.saving"),
    widgetsPanel: t("datox.dashboard.widgets"),
    data: t("datox.dashboard.data"),
    design: t("datox.dashboard.design"),
    searchWidgets: t("datox.dashboard.searchWidgets"),
    listView: t("datox.dashboard.listView"),
    gridView: t("datox.dashboard.gridView"),
    grid: t("datox.listing.grid"),
    outputTable: t("datox.dashboard.dataset"),
    source: t("datox.dashboard.source"),
    openFile: t("ui.sessionReview.openFile"),
    loading: t("common.loading"),
    requestFailed: t("common.requestFailed"),
    rows: t("datox.dashboard.rows"),
    columns: t("datox.dashboard.columns"),
    tableMetadata: t("datox.dashboard.tableMetadata"),
    columnMetadata: t("datox.dashboard.columnMetadata"),
    tablePreview: t("datox.dashboard.tablePreview"),
    notAssessed: t("datox.risk.indicator.unassessed"),
    closePanel: t("datox.dashboard.closePanel"),
    openPanel: t("datox.dashboard.openPanel"),
    panel: t("datox.dashboard.panel"),
    newTabName: t("datox.dashboard.newTabName"),
    widgetLibrary: t("datox.dashboard.widgetLibrary"),
    dashboardName: t("datox.dashboard.name"),
    dashboardNamePlaceholder: t("datox.dashboard.namePlaceholder"),
    defaultTabName: t("datox.dashboard.defaultTabName"),
    emptyCanvasTitle: t("datox.dashboard.emptyCanvasTitle"),
    emptyCanvasDescription: t("datox.dashboard.emptyCanvasDescription"),
    accentColor: t("datox.dashboard.accentColor"),
    selectedWidgetTitle: t("datox.dashboard.selectedWidgetTitle"),
    textContent: t("datox.dashboard.textContent"),
    titleContent: t("datox.dashboard.titleContent"),
    editText: t("datox.dashboard.editText"),
    widgetDataset: t("datox.dashboard.widgetDataset"),
    metricColumn: t("datox.dashboard.metricColumn"),
    summarize: t("datox.dashboard.summarize"),
    aggregation: t("datox.dashboard.aggregation"),
    aggregationSum: t("datox.dashboard.aggregationSum"),
    aggregationAverage: t("datox.dashboard.aggregationAverage"),
    aggregationMin: t("datox.dashboard.aggregationMin"),
    aggregationMax: t("datox.dashboard.aggregationMax"),
    aggregationCount: t("datox.dashboard.aggregationCount"),
    aggregationFirst: t("datox.dashboard.aggregationFirst"),
    aggregationLast: t("datox.dashboard.aggregationLast"),
    labelColumn: t("datox.dashboard.labelColumn"),
    editWidget: t("datox.dashboard.editWidget"),
    renameWidget: t("datox.dashboard.renameWidget"),
    editData: t("datox.dashboard.editData"),
    editDesign: t("datox.dashboard.editDesign"),
    exportTable: t("datox.dashboard.exportTable"),
    fullView: t("datox.dashboard.fullView"),
    widgetType: t("datox.dashboard.widgetType"),
    sampleData: t("datox.dashboard.sampleData"),
    filter: t("datox.dashboard.filter"),
    addFilter: t("datox.dashboard.addFilter"),
    noFiltersApplied: t("datox.dashboard.noFiltersApplied"),
    filterContains: t("datox.dashboard.filterContains"),
    filterEquals: t("datox.dashboard.filterEquals"),
    filterNotEmpty: t("datox.dashboard.filterNotEmpty"),
    filterNotApplied: t("datox.dashboard.filterNotApplied"),
    filterOperator: t("datox.dashboard.filterOperator"),
    filterDateOn: t("datox.dashboard.filterDateOn"),
    filterDateNotOn: t("datox.dashboard.filterDateNotOn"),
    filterDateBefore: t("datox.dashboard.filterDateBefore"),
    filterDateOnOrBefore: t("datox.dashboard.filterDateOnOrBefore"),
    filterDateAfter: t("datox.dashboard.filterDateAfter"),
    filterDateOnOrAfter: t("datox.dashboard.filterDateOnOrAfter"),
    filterSearch: t("datox.dashboard.filterSearch"),
    filterEveryWidget: t("datox.dashboard.filterEveryWidget"),
    filterEveryWidgetHelp: t("datox.dashboard.filterEveryWidgetHelp"),
    filterNotSet: t("datox.dashboard.filterNotSet"),
    filterSetUp: t("datox.dashboard.filterSetUp"),
    pieNegative: t("datox.dashboard.pieNegative"),
    bindingMissingColumn: t("datox.dashboard.bindingMissingColumn"),
    bindingNotNumeric: t("datox.dashboard.bindingNotNumeric"),
    explainFilters: t("datox.dashboard.explainFilters"),
    explainPageFilter: t("datox.dashboard.explainPageFilter"),
    explainIs: t("datox.dashboard.explainIs"),
    explainIsNot: t("datox.dashboard.explainIsNot"),
    explainIsOneOf: t("datox.dashboard.explainIsOneOf"),
    explainContains: t("datox.dashboard.explainContains"),
    explainNotEmpty: t("datox.dashboard.explainNotEmpty"),
    smartTitleOf: t("datox.dashboard.smartTitleOf"),
    smartTitleBy: t("datox.dashboard.smartTitleBy"),
    smartTitleByOnly: t("datox.dashboard.smartTitleByOnly"),
    smartTitleCount: t("datox.dashboard.smartTitleCount"),
    smartTitleVs: t("datox.dashboard.smartTitleVs"),
    stackGrouped: t("datox.dashboard.stackGrouped"),
    stackStacked: t("datox.dashboard.stackStacked"),
    stackPercent: t("datox.dashboard.stackPercent"),
    stackPercentHelp: t("datox.dashboard.stackPercentHelp"),
    seriesSettings: t("datox.dashboard.seriesSettings"),
    seriesDraw: t("datox.dashboard.seriesDraw"),
    seriesBars: t("datox.dashboard.seriesBars"),
    seriesLine: t("datox.dashboard.seriesLine"),
    seriesArea: t("datox.dashboard.seriesArea"),
    seriesRightAxis: t("datox.dashboard.seriesRightAxis"),
    seriesRightAxisVertical: t("datox.dashboard.seriesRightAxisVertical"),
    filterMore: t("datox.dashboard.filterMore"),
    chartOther: t("datox.dashboard.chartOther"),
    chartTopOf: t("datox.dashboard.chartTopOf"),
    limitCategories: t("datox.dashboard.limitCategories"),
    limitOtherHelp: t("datox.dashboard.limitOtherHelp"),
    limitRankedHelp: t("datox.dashboard.limitRankedHelp"),
    headerShowDate: t("datox.dashboard.headerShowDate"),
    headerSyncHelp: t("datox.dashboard.headerSyncHelp"),
    headerDataTo: t("datox.dashboard.headerDataTo"),
    barDisplay: t("datox.dashboard.barDisplay"),
    barDisplayBars: t("datox.dashboard.barDisplayBars"),
    barDisplayContributors: t("datox.dashboard.barDisplayContributors"),
    barDisplayDetractors: t("datox.dashboard.barDisplayDetractors"),
    percentChangeFormat: t("datox.dashboard.percentChangeFormat"),
    filterTargets: t("datox.dashboard.filterTargets"),
    sort: t("datox.dashboard.sort"),
    noSort: t("datox.dashboard.noSort"),
    sortAscending: t("datox.dashboard.sortAscending"),
    sortDescending: t("datox.dashboard.sortDescending"),
    rowLimit: t("datox.dashboard.rowLimit"),
    noRowLimit: t("datox.dashboard.noRowLimit"),
    chartData: t("datox.dashboard.chartData"),
    groupBy: t("datox.dashboard.groupBy"),
    groupByHelp: t("datox.dashboard.groupByHelp"),
    dataStreams: t("datox.dashboard.dataStreams"),
    dataStreamsHelp: t("datox.dashboard.dataStreamsHelp"),
    dataStreamDisplayNames: t("datox.dashboard.dataStreamDisplayNames"),
    showTop: t("datox.dashboard.showTop"),
    by: t("datox.dashboard.by"),
    order: t("datox.dashboard.order"),
    logo: t("datox.dashboard.logo"),
    uploadLogo: t("datox.dashboard.uploadLogo"),
    headerColor: t("datox.dashboard.headerColor"),
    // §75: the sheet's style (Brand).
    sheetStyle: t("datox.v2.sheet.style"),
    sheetStyles: { swiss: t("datox.v2.sheet.style.swiss"), cards: t("datox.v2.sheet.style.cards"), bold: t("datox.v2.sheet.style.bold"), lesha: t("datox.v2.sheet.style.lesha") },
    cornerRadius: t("datox.dashboard.cornerRadius"),
    radiusSquare: t("datox.dashboard.radiusSquare"),
    radiusMedium: t("datox.dashboard.radiusMedium"),
    radiusLarge: t("datox.dashboard.radiusLarge"),
    fontSize: t("datox.dashboard.fontSize"),
    fontWeight: t("datox.dashboard.fontWeight"),
    textColor: t("datox.dashboard.textColor"),
    textAlign: t("datox.dashboard.textAlign"),
    verticalAlign: t("datox.dashboard.verticalAlign"),
    alignLeft: t("datox.dashboard.alignLeft"),
    alignCenter: t("datox.dashboard.alignCenter"),
    alignRight: t("datox.dashboard.alignRight"),
    alignTop: t("datox.dashboard.alignTop"),
    alignMiddle: t("datox.dashboard.alignMiddle"),
    alignBottom: t("datox.dashboard.alignBottom"),
    fontCompact: t("datox.dashboard.fontCompact"),
    fontNormal: t("datox.dashboard.fontNormal"),
    fontLarge: t("datox.dashboard.fontLarge"),
    headingFont: t("datox.dashboard.headingFont"),
    bodyFont: t("datox.dashboard.bodyFont"),
    widgetShadows: t("datox.dashboard.widgetShadows"),
    lightGrayBackground: t("datox.dashboard.lightGrayBackground"),
    tableDensity: t("datox.dashboard.tableDensity"),
    ...Object.fromEntries(
      (
        [
          "paginateRows",
          "pageSize",
          "previousPage",
          "nextPage",
          "pageStatus",
          "showSummary",
          "summaryOperation",
          "summaryScope",
          "summaryLabel",
          "columnDisplayName",
          "autoFormat",
          "textFormat",
          "none",
          "scatterX",
          "scatterY",
          "scatterSize",
          "scatterBindingSummary",
          "noBinding",
          "invalidScatterBindings",
          "invalidChartBindings",
          "datasetLoadError",
          "editFreeStyle",
          "freeStyleLimit",
          "showRowNumbers",
          "highlightHeaderRow",
          "columnName",
          "rowName",
          "columnWidth",
          "cellAlignment",
          "addColumn",
          "removeColumn",
          "addRow",
          "removeRow",
          "normalRow",
          "sectionRow",
          "columnFormatting",
          "sourceColumn",
          "formatPreview",
          "percentFormatHelp",
          "columnSummary",
          "paginationSettings",
          "summaryScopeShort",
          "rowRange",
          "goToPage",
          "noMatchingRows",
          "tableContent",
          "tableDimensions",
          "editTableContent",
          "independentTableHelp",
          "gridEditingHelp",
          "cellPosition",
          "selectedCellSettings",
          "gridSaveHelp",
          "doneEditing",
          "tableAppearance",
          "numberFormatting",
          "chartAxes",
          "resultLimit",
          "filtersAndSorting",
          "resultLimitHelp",
          "scatterSizeNone",
          "numericBindingRequired",
          "scatterSameAxes",
          "conversionHelp",
          "tableLoading",
          "retryDataset",
          "selectDatasetHelp",
          "rowNumber",
        ] as const
      ).map((key) => [key, t(`datox.dashboard.${key}`)]),
    ),
    showTableHeader: t("datox.dashboard.showTableHeader"),
    stripedRows: t("datox.dashboard.stripedRows"),
    headerFill: t("datox.dashboard.headerFill"),
    filterDisplay: t("datox.dashboard.filterDisplay"),
    filterDisplayChips: t("datox.dashboard.filterDisplayChips"),
    filterDisplayList: t("datox.dashboard.filterDisplayList"),
    filterDisplayDropdown: t("datox.dashboard.filterDisplayDropdown"),
    allowMultipleValues: t("datox.dashboard.allowMultipleValues"),
    chartOrientation: t("datox.dashboard.chartOrientation"),
    chartType: t("datox.dashboard.chartType"),
    chartSection: t("datox.dashboard.chartSection"),
    labelsSection: t("datox.dashboard.labelsSection"),
    donutChart: t("datox.dashboard.donutChart"),
    showValuesNextToLabels: t("datox.dashboard.showValuesNextToLabels"),
    vertical: t("datox.dashboard.vertical"),
    horizontal: t("datox.dashboard.horizontal"),
    showLegend: t("datox.dashboard.showLegend"),
    showValueLabels: t("datox.dashboard.showValueLabels"),
    showAxisLabels: t("datox.dashboard.showAxisLabels"),
    showGridLines: t("datox.dashboard.showGridLines"),
    distinctBarColors: t("datox.dashboard.distinctBarColors"),
    stackSeries: t("datox.dashboard.stackSeries"),
    xAxisLabel: t("datox.dashboard.xAxisLabel"),
    yAxisLabel: t("datox.dashboard.yAxisLabel"),
    valueFormat: t("datox.dashboard.valueFormat"),
    numberFormat: t("datox.dashboard.numberFormat"),
    integerFormat: t("datox.dashboard.integerFormat"),
    percentFormat: t("datox.dashboard.percentFormat"),
    decimalPlaces: t("datox.dashboard.decimalPlaces"),
    duplicateWidget: t("datox.dashboard.duplicateWidget"),
    deleteWidget: t("datox.dashboard.deleteWidget"),
    copySuffix: t("datox.dashboard.copySuffix"),
    metricFallback: t("datox.dashboard.metricFallback"),
    itemLabel: t("datox.dashboard.itemLabel"),
    valueLabel: t("datox.dashboard.valueLabel"),
    kpiComparisonEnabled: t("datox.dashboard.kpiComparisonEnabled"),
    kpiComparisonPreviousDashboard: t("datox.dashboard.kpiComparisonPreviousDashboard"),
    kpiComparisonPreviousKpi: t("datox.dashboard.kpiComparisonPreviousKpi"),
    kpiComparisonChangeMode: t("datox.dashboard.kpiComparisonChangeMode"),
    kpiComparisonVisual: t("datox.dashboard.kpiComparisonVisual"),
    kpiComparisonDirection: t("datox.dashboard.kpiComparisonDirection"),
    kpiComparisonBoth: t("datox.dashboard.kpiComparisonBoth"),
    kpiComparisonAmount: t("datox.dashboard.kpiComparisonAmount"),
    kpiComparisonPercentage: t("datox.dashboard.kpiComparisonPercentage"),
    kpiComparisonBars: t("datox.dashboard.kpiComparisonBars"),
    kpiComparisonSlope: t("datox.dashboard.kpiComparisonSlope"),
    kpiComparisonHigherIsBetter: t("datox.dashboard.kpiComparisonHigherIsBetter"),
    kpiComparisonLowerIsBetter: t("datox.dashboard.kpiComparisonLowerIsBetter"),
    kpiComparisonNeutral: t("datox.dashboard.kpiComparisonNeutral"),
    kpiComparisonReload: t("datox.dashboard.kpiComparisonReload"),
    kpiComparisonNoPreviousPeriod: t("datox.dashboard.kpiComparisonNoPreviousPeriod"),
    kpiComparisonPreviousNotPublished: t("datox.dashboard.kpiComparisonPreviousNotPublished"),
    kpiComparisonPreviousDashboardMissing: t(
      "datox.dashboard.kpiComparisonPreviousDashboardMissing",
    ),
    kpiComparisonNoPublishedValue: t("datox.dashboard.kpiComparisonNoPublishedValue"),
    kpiComparisonCurrentUnavailable: t("datox.dashboard.kpiComparisonCurrentUnavailable"),
    kpiComparisonUnavailable: t("datox.dashboard.kpiComparisonUnavailable"),
    kpiComparisonNeedsSelection: t("datox.dashboard.kpiComparisonNeedsSelection"),
    kpiComparisonUnitMismatch: t("datox.dashboard.kpiComparisonUnitMismatch"),
    kpiComparisonPercentageUnavailable: t("datox.dashboard.kpiComparisonPercentageUnavailable"),
    kpiComparisonStale: t("datox.dashboard.kpiComparisonStale"),
    kpiComparisonStaleReload: t("datox.dashboard.kpiComparisonStaleReload"),
    kpiComparisonSourcesUnavailable: t("datox.dashboard.kpiComparisonSourcesUnavailable"),
    textPlaceholder: t("datox.dashboard.textPlaceholder"),
    portrait: t("datox.dashboard.portrait"),
    landscape: t("datox.dashboard.landscape"),
    undo: t("datox.dashboard.undo"),
    redo: t("datox.dashboard.redo"),
    drag: t("datox.dashboard.drag"),
    more: t("datox.dashboard.more"),
    widgetCount: t("datox.dashboard.widgetCount"),
    tabCount: t("datox.dashboard.tabCount"),
    allChangesSaved: t("datox.dashboard.allChangesSaved"),
    somethingWentWrong: t("datox.dashboard.somethingWentWrong"),
    cancel: t("common.cancel"),
    widgets: {
      header: t("datox.dashboard.widget.header"),
      text: t("datox.dashboard.widget.text"),
      title: t("datox.dashboard.widget.title"),
      kpi: t("datox.dashboard.widget.kpi"),
      table: t("datox.dashboard.widget.table"),
      freeTable: t("datox.dashboard.widget.freeTable"),
      bar: t("datox.dashboard.widget.bar"),
      line: t("datox.dashboard.widget.line"),
      scatter: t("datox.dashboard.widget.scatter"),
      pie: t("datox.dashboard.widget.pie"),
      heatmap: t("datox.dashboard.widget.heatmap"),
      multiSeries: t("datox.dashboard.widget.multiSeries"),
      treemap: t("datox.dashboard.widget.treemap"),
      funnel: t("datox.dashboard.widget.funnel"),
      filter: t("datox.dashboard.widget.filter"),
    },
  }
}
